/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  githubAuthApiRef,
  identityApiRef,
  analyticsApiRef,
} from '@backstage/core-plugin-api';
import { QuantumMetric } from '@quantum-metric/plugin-analytics-module-qm';
import { ScmAuth, ScmIntegrationsApi, scmAuthApiRef, scmIntegrationsApiRef } from '@backstage/integration-react';
import {
  restClientRef,
  aaPingSSOAuthApiRef,
  apis as devkitApis,
  ObserveServiceApiRef,
  Squad360ServiceApiRef,
  emuAuthApiRef,
  unifiedGithubApiRef,
} from '@runway/devkit';
import {
  ADOService,
  adoServiceApiRef,
  GithubCopilotAccessService,
  GithubCopilotAccessApiRef,
  GithubEnterpriseAccessService,
  GithubEnterpriseAccessApiRef,
  DeaService,
  deaServiceApiRef,
  GhecRepoAdmins,
  ghecRepoAdminsFormApiRef,
  PFAdminApiService,
  pfAdminServiceApiRef,
  SurveysAccessService,
  surveysServiceApiRef,
  VdiDesktopService,
  vdiDesktopServiceApiRef,
  VdiPoolListService,
  vdiPoolListServiceApiRef,
} from '@runway/plugin-access-control';
import {
  ApigeeProjectListQueryApiRef,
  ApigeeProjectsList,
  ApimBackendService,
  ApimBackendServiceApiRef,
} from '@runway/plugin-apigee-ui';
import {
  ArgoCDService,
  ArgoCDServiceApiRef,
  CatalogProjectsService,
  catalogProjectsServiceApiRef,
  containerImagePackagesApiRef,
  ContainerImagePackagesService,
} from '@runway/plugin-catalog-customizations';
import { EMFTBackendService, EMFTBackendServiceApiRef } from '@runway/plugin-emft-ui';
import { cloudsmithServiceApiRef, CloudsmithService } from '@runway/plugin-infrastructure';
import { OrionService, orionServiceApiRef } from '@runway/plugin-orion-auto';
import { TowerDataService, towerDataServiceApiRef } from '@runway/plugin-tower-api';
import { Tracker, trackerApiRef } from './tracker';
import { OrionRegistrationService, orionRegistrationServiceApiRef } from '@runway/plugin-orion-ui';
import { SwaggerService, SwaggerServiceApiRef } from '@runway/plugin-swagger';
import { DiffServiceApiRef, DiffService } from '@runway/plugin-diff-tool';
import { GraphqlProjectListQueryApiRef, GraphqlProjectsList } from '@runway/plugin-graphql';

export const apis: AnyApiFactory[] = [
  ...devkitApis,
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  // Custom ScmAuthApi that merges the standard github.com (GHEC) provider
  // with the GitHub EMU provider, enabling the Scaffolder
  // and Catalog Import plugins to authenticate against both hosts.
  createApiFactory({
    api: scmAuthApiRef,
    deps: {
      githubAuthApi: githubAuthApiRef,
      emuAuthApi: emuAuthApiRef,
      configApi: configApiRef,
    },
    factory: ({ githubAuthApi, emuAuthApi, configApi }) => {
      const emuHost = configApi.getOptionalString('github.emu.host');
      if (emuHost) {
        return ScmAuth.merge(ScmAuth.forGithub(githubAuthApi), ScmAuth.forGithub(emuAuthApi, { host: emuHost }));
      }
      return ScmAuth.forGithub(githubAuthApi);
    },
  }),
  createApiFactory({
    api: ArgoCDServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      authApi: aaPingSSOAuthApiRef,
    },
    factory: ({ configApi, identityApi, authApi }) =>
      new ArgoCDService(
        configApi.getString('backend.baseUrl'),
        identityApi,
        authApi,
        configApi.getString('clusterService.baseUrl'),
      ),
  }),
  createApiFactory({
    api: containerImagePackagesApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ configApi, identityApi }) =>
      new ContainerImagePackagesService(configApi.getString('backend.baseUrl'), identityApi),
  }),
  createApiFactory({
    api: ApimBackendServiceApiRef,
    deps: {
      configApi: configApiRef,
      githubAuthApi: githubAuthApiRef,
      squad360Api: Squad360ServiceApiRef,
      oAuthApi: aaPingSSOAuthApiRef,
      emuAuthApi: emuAuthApiRef,
    },
    factory: ({ configApi, githubAuthApi, squad360Api, oAuthApi, emuAuthApi }) =>
      new ApimBackendService(
        configApi.getString('github.ghec.url'),
        configApi.getString('apigee.backendUrl'),
        githubAuthApi,
        squad360Api,
        oAuthApi,
        emuAuthApi,
      ),
  }),
  createApiFactory({
    api: ApigeeProjectListQueryApiRef,
    deps: {
      configApi: configApiRef,
      githubAuthApi: githubAuthApiRef,
      emuAuthApi: emuAuthApiRef,
    },
    factory: ({ configApi, githubAuthApi, emuAuthApi }) =>
      new ApigeeProjectsList(configApi.getString('github.ghec.graphUrl'), githubAuthApi, emuAuthApi),
  }),
  createApiFactory({
    api: GraphqlProjectListQueryApiRef,
    deps: {
      unifiedGithubApi: unifiedGithubApiRef,
    },
    factory: ({ unifiedGithubApi }) => new GraphqlProjectsList(unifiedGithubApi),
  }),
  createApiFactory({
    api: towerDataServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      restClient: restClientRef,
    },
    factory: ({ configApi, identityApi, restClient }) =>
      new TowerDataService(configApi.getString('backend.baseUrl'), identityApi, restClient),
  }),
  createApiFactory({
    api: deaServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ configApi, identityApi }) => new DeaService(configApi.getString('backend.baseUrl'), identityApi),
  }),
  createApiFactory({
    api: adoServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ configApi, identityApi }) => new ADOService(configApi.getString('backend.baseUrl'), identityApi),
  }),
  createApiFactory({
    api: catalogProjectsServiceApiRef,
    deps: {
      unifiedGithubApi: unifiedGithubApiRef,
    },
    factory: ({ unifiedGithubApi }) => new CatalogProjectsService(unifiedGithubApi),
  }),
  createApiFactory({
    api: trackerApiRef,
    deps: {
      oAuthApi: aaPingSSOAuthApiRef,
      observeServiceApi: ObserveServiceApiRef,
    },
    factory: ({ oAuthApi, observeServiceApi }) => new Tracker(oAuthApi, observeServiceApi),
  }),
  createApiFactory({
    api: DiffServiceApiRef,
    deps: {
      unifiedGithubApi: unifiedGithubApiRef,
    },
    factory: ({ unifiedGithubApi }) => new DiffService(unifiedGithubApi),
  }),
  createApiFactory({
    api: GithubEnterpriseAccessApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      restClient: restClientRef,
    },
    factory: ({ configApi, identityApi, restClient }) =>
      new GithubEnterpriseAccessService(configApi.getString('backend.baseUrl'), identityApi, restClient),
  }),
  createApiFactory({
    api: ghecRepoAdminsFormApiRef,
    deps: {
      configApi: configApiRef,
      oAuthApi: aaPingSSOAuthApiRef,
    },
    factory: ({ configApi, oAuthApi }) => new GhecRepoAdmins(configApi.getString('repoAdmins.url'), oAuthApi),
  }),
  createApiFactory({
    api: vdiDesktopServiceApiRef,
    deps: {
      configApi: configApiRef,
      oAuthApi: aaPingSSOAuthApiRef,
    },
    factory: ({ configApi, oAuthApi }) => new VdiDesktopService(oAuthApi, configApi.getString('virtualDesktop.url')),
  }),
  createApiFactory({
    api: vdiPoolListServiceApiRef,
    deps: {
      configApi: configApiRef,
      oAuthApi: aaPingSSOAuthApiRef,
    },
    factory: ({ configApi, oAuthApi }) => new VdiPoolListService(oAuthApi, configApi.getString('virtualDesktop.url')),
  }),
  createApiFactory({
    api: EMFTBackendServiceApiRef,
    deps: {
      configApi: configApiRef,
      oAuthApi: aaPingSSOAuthApiRef,
    },
    factory: ({ configApi, oAuthApi }) => new EMFTBackendService(oAuthApi, configApi.getString('emftService.url')),
  }),
  createApiFactory({
    api: orionServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      restClient: restClientRef,
    },
    factory: ({ configApi, identityApi, restClient }) =>
      new OrionService(configApi.getString('backend.baseUrl'), identityApi, restClient),
  }),
  createApiFactory({
    api: surveysServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ configApi, identityApi }) =>
      new SurveysAccessService(
        configApi.getString('backend.baseUrl'),
        {
          prod: configApi.getString('surveys.prod.appUrl'),
          non_prod: configApi.getString('surveys.non_prod.appUrl'),
        },
        identityApi,
      ),
  }),
  createApiFactory({
    api: orionRegistrationServiceApiRef,
    deps: {
      configApi: configApiRef,
      oAuthApi: aaPingSSOAuthApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ configApi, oAuthApi, identityApi }) =>
      new OrionRegistrationService(configApi.getString('backend.baseUrl'), identityApi, oAuthApi),
  }),
  createApiFactory({
    api: pfAdminServiceApiRef,
    deps: {
      configApi: configApiRef,
      oAuthApi: aaPingSSOAuthApiRef,
    },
    factory: ({ configApi, oAuthApi }) => new PFAdminApiService(configApi.get('pfadmin'), oAuthApi),
  }),
  createApiFactory({
    api: SwaggerServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ configApi, identityApi }) => new SwaggerService(configApi.get('backend.baseUrl'), identityApi),
  }),
  createApiFactory({
    api: cloudsmithServiceApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      restClient: restClientRef,
    },
    factory: ({ configApi, identityApi, restClient }) =>
      new CloudsmithService(configApi.getString('backend.baseUrl'), identityApi, restClient),
  }),
  createApiFactory({
    api: GithubCopilotAccessApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      restClient: restClientRef,
    },
    factory: ({ configApi, identityApi, restClient }) =>
      new GithubCopilotAccessService(configApi.getString('backend.baseUrl'), identityApi, restClient),
  }),
  createApiFactory({
    api: analyticsApiRef,
    deps: { configApi: configApiRef, identityApi: identityApiRef },
    factory: ({ configApi, identityApi }) =>
      QuantumMetric.fromConfig(configApi, {
        identityApi,
      }),
  }),
];
